import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { action } from 'mobx'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { Form, FormGroupContainer, TextInputContainer } from 'components/inputs'
import { masks } from 'utils/input-mask-util'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { CountryInput } from 'components/inputs/CountryInput'
import { TabBar, TabItem } from 'components/TabBar'
import EditPersonalProfileDialogViewModel, {
  TAB_LABELS,
} from './EditPersonalProfileDialogViewModel'
import {
  InfoGatheringDialogFieldGrid,
  InfoGatheringDialogScrollArea,
  InfoGatheringDialogSubsectionHeader,
} from 'jobs/info-gathering/components/InfoGatheringDialog'
import styles from './EditPersonalProfileDialog.sass'

interface Props {
  vm: EditPersonalProfileDialogViewModel
}

export default observer(function EditPersonalProfileDialog({ vm }: Props) {
  const personalEditor = vm.personalProfileEditorVm
  const personalInfoVm = personalEditor.personalInfoVm
  const contactInfoVm = personalEditor.contactInfoVm
  const physicalAddressVm = contactInfoVm.physicalAddressVm

  const spousalEditor = vm.spousalProfileEditorVm
  const spousePersonalInfoVm = spousalEditor.personalInfoVm

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
      disableCloseOnOutsideClick
      disableCloseOnEsc
    >
      <ModalSheetHeader withCloseButton>
        <ModalSheetTitle emphasized>Fill in personal info</ModalSheetTitle>
      </ModalSheetHeader>
      <TabBar bordered>
        {vm.availableTabs.map((tab) => (
          <TabItem
            key={tab}
            active={vm.activeTab === tab}
            onClick={() => vm.setTab(tab)}
            dataTest={`edit-personal-tab-${tab}`}
          >
            {TAB_LABELS[tab]}
          </TabItem>
        ))}
      </TabBar>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <InfoGatheringDialogScrollArea>
            <Form>
              {vm.activeTab === 'personal' && (
                <>
                  <InfoGatheringDialogSubsectionHeader>
                    Personal information
                  </InfoGatheringDialogSubsectionHeader>
                  <InfoGatheringDialogFieldGrid>
                    <TextInputContainer
                      model={personalInfoVm}
                      field="firstName"
                      name="firstName"
                      label="First name"
                      error={personalInfoVm.validation.getError('firstName')}
                      data-test="edit-personal-first-name"
                    />
                    <TextInputContainer
                      model={personalInfoVm}
                      field="middleInitial"
                      name="middleInitial"
                      label="Middle initial"
                      sublabel="(optional)"
                      maxLength={1}
                      error={personalInfoVm.validation.getError(
                        'middleInitial',
                      )}
                      data-test="edit-personal-middle-initial"
                    />
                    <TextInputContainer
                      model={personalInfoVm}
                      field="lastName"
                      name="lastName"
                      label="Last name"
                      error={personalInfoVm.validation.getError('lastName')}
                      data-test="edit-personal-last-name"
                    />
                    <TextInputContainer
                      model={personalInfoVm}
                      field="dateOfBirth"
                      name="dateOfBirth"
                      label="Date of birth"
                      placeholder="MM/DD/YYYY"
                      mask={masks.americanDate}
                      error={personalInfoVm.validation.getError('dateOfBirth')}
                      data-test="edit-personal-dob"
                    />
                    <TextInputContainer
                      model={personalInfoVm}
                      field="personalTaxpayerIdentificationNumber"
                      name="personalTaxpayerIdentificationNumber"
                      label="SSN or ITIN"
                      placeholder="123-45-6789"
                      mask={masks.ssnOrItin}
                      error={personalInfoVm.validation.getError(
                        'personalTaxpayerIdentificationNumber',
                      )}
                      data-test="edit-personal-tax-id"
                    />
                    <TextInputContainer
                      model={personalEditor}
                      field="occupation"
                      name="occupation"
                      label="Occupation"
                      error={personalEditor.validation.getError('occupation')}
                      data-test="edit-personal-occupation"
                    />
                  </InfoGatheringDialogFieldGrid>

                  <InfoGatheringDialogSubsectionHeader bordered>
                    Contact information
                  </InfoGatheringDialogSubsectionHeader>
                  <InfoGatheringDialogFieldGrid>
                    <TextInputContainer
                      model={contactInfoVm}
                      field="email"
                      name="email"
                      label="Email"
                      placeholder="name@example.com"
                      controlType="email"
                      error={contactInfoVm.validation.getError('email')}
                      data-test="edit-personal-email"
                    />
                    <TextInputContainer
                      model={contactInfoVm}
                      field="phoneNumber"
                      name="phoneNumber"
                      label="Phone number"
                      placeholder="(555) 123-4567"
                      controlType="tel"
                      error={contactInfoVm.validation.getError('phoneNumber')}
                      data-test="edit-personal-phone"
                    />
                    <TextInputContainer
                      model={physicalAddressVm}
                      field="addressLine1"
                      name="addressLine1"
                      label="Address line 1"
                      error={physicalAddressVm.validation.getError(
                        'addressLine1',
                      )}
                      data-test="edit-personal-address-line1"
                    />
                    <TextInputContainer
                      model={physicalAddressVm}
                      field="addressLine2"
                      name="addressLine2"
                      label="Address line 2"
                      sublabel="(optional)"
                      error={physicalAddressVm.validation.getError(
                        'addressLine2',
                      )}
                      data-test="edit-personal-address-line2"
                    />
                    <TextInputContainer
                      model={physicalAddressVm}
                      field="city"
                      name="city"
                      label="City"
                      error={physicalAddressVm.validation.getError('city')}
                      data-test="edit-personal-city"
                    />
                    <TextInputContainer
                      model={physicalAddressVm}
                      field="region"
                      name="region"
                      label="State / Region"
                      error={physicalAddressVm.validation.getError('region')}
                      data-test="edit-personal-region"
                    />
                    <TextInputContainer
                      model={physicalAddressVm}
                      field="postalCode"
                      name="postalCode"
                      label="Postal code"
                      error={physicalAddressVm.validation.getError(
                        'postalCode',
                      )}
                      data-test="edit-personal-postal-code"
                    />
                    <FormGroupContainer
                      label="Country"
                      htmlFor="country"
                      error={physicalAddressVm.validation.getError('country')}
                    >
                      <CountryInput
                        value={physicalAddressVm.country}
                        onChange={action((code: string) => {
                          physicalAddressVm.country = code
                        })}
                        data-test="edit-personal-country"
                      />
                    </FormGroupContainer>
                  </InfoGatheringDialogFieldGrid>
                </>
              )}

              {vm.activeTab === 'spousal' && (
                <>
                  <InfoGatheringDialogSubsectionHeader>
                    Spousal information
                  </InfoGatheringDialogSubsectionHeader>
                  <InfoGatheringDialogFieldGrid>
                    <TextInputContainer
                      model={spousePersonalInfoVm}
                      field="firstName"
                      name="spouseFirstName"
                      label="First name"
                      error={spousePersonalInfoVm.validation.getError(
                        'firstName',
                      )}
                      data-test="edit-spouse-first-name"
                    />
                    <TextInputContainer
                      model={spousePersonalInfoVm}
                      field="middleInitial"
                      name="spouseMiddleInitial"
                      label="Middle initial"
                      sublabel="(optional)"
                      maxLength={1}
                      error={spousePersonalInfoVm.validation.getError(
                        'middleInitial',
                      )}
                      data-test="edit-spouse-middle-initial"
                    />
                    <TextInputContainer
                      model={spousePersonalInfoVm}
                      field="lastName"
                      name="spouseLastName"
                      label="Last name"
                      error={spousePersonalInfoVm.validation.getError(
                        'lastName',
                      )}
                      data-test="edit-spouse-last-name"
                    />
                    <TextInputContainer
                      model={spousePersonalInfoVm}
                      field="dateOfBirth"
                      name="spouseDateOfBirth"
                      label="Date of birth"
                      placeholder="MM/DD/YYYY"
                      mask={masks.americanDate}
                      error={spousePersonalInfoVm.validation.getError(
                        'dateOfBirth',
                      )}
                      data-test="edit-spouse-dob"
                    />
                    <TextInputContainer
                      model={spousePersonalInfoVm}
                      field="personalTaxpayerIdentificationNumber"
                      name="spousePersonalTaxpayerIdentificationNumber"
                      label="SSN or ITIN"
                      placeholder="123-45-6789"
                      mask={masks.ssnOrItin}
                      error={spousePersonalInfoVm.validation.getError(
                        'personalTaxpayerIdentificationNumber',
                      )}
                      data-test="edit-spouse-tax-id"
                    />
                    <TextInputContainer
                      model={spousalEditor}
                      field="occupation"
                      name="spouseOccupation"
                      label="Occupation"
                      sublabel="(optional)"
                      error={spousalEditor.validation.getError('occupation')}
                      data-test="edit-spouse-occupation"
                    />
                    <TextInputContainer
                      model={spousalEditor}
                      field="email"
                      name="spouseEmail"
                      label="Email address"
                      placeholder="name@example.com"
                      controlType="email"
                      error={spousalEditor.validation.getError('email')}
                      data-test="edit-spouse-email"
                    />
                  </InfoGatheringDialogFieldGrid>
                </>
              )}

              {vm.activeTab === 'dependents' && (
                <>
                  {vm.dependentProfileEditors.length === 0 ? (
                    <div className={styles.dependentsEmptyState}>
                      <div className={styles.dependentsEmptyStatePrompt}>
                        Does the client have any dependents to claim?
                      </div>
                      <Button
                        type="secondary"
                        onClick={vm.addDependent}
                        disabled={vm.submit.pending}
                        data-test="edit-personal-add-dependent-button"
                      >
                        + Add New Dependent
                      </Button>
                    </div>
                  ) : (
                    <>
                      {vm.dependentProfileEditors.map((editor, index) => (
                        <div
                          key={index}
                          className={cx(
                            styles.dependentRow,
                            index > 0 && styles.dependentRowBordered,
                          )}
                        >
                          <div className={styles.dependentRowHeader}>
                            <div className={styles.dependentRowTitle}>
                              Dependent #{index + 1}
                            </div>
                            <Button
                              type="text"
                              className={styles.dependentRowRemoveButton}
                              onClick={() => vm.removeDependentAt(index)}
                              disabled={vm.submit.pending}
                              data-test={`edit-personal-remove-dependent-${index}`}
                            >
                              <Icon name="delete" /> Remove
                            </Button>
                          </div>
                          <InfoGatheringDialogFieldGrid>
                            <TextInputContainer
                              model={editor.personalInfoVm}
                              field="firstName"
                              name={`dependent-${index}-firstName`}
                              label="First name"
                              error={editor.personalInfoVm.validation.getError(
                                'firstName',
                              )}
                              data-test={`edit-dependent-${index}-first-name`}
                            />
                            <TextInputContainer
                              model={editor.personalInfoVm}
                              field="middleInitial"
                              name={`dependent-${index}-middleInitial`}
                              label="Middle initial"
                              sublabel="(optional)"
                              maxLength={1}
                              error={editor.personalInfoVm.validation.getError(
                                'middleInitial',
                              )}
                              data-test={`edit-dependent-${index}-middle-initial`}
                            />
                            <TextInputContainer
                              model={editor.personalInfoVm}
                              field="lastName"
                              name={`dependent-${index}-lastName`}
                              label="Last name"
                              error={editor.personalInfoVm.validation.getError(
                                'lastName',
                              )}
                              data-test={`edit-dependent-${index}-last-name`}
                            />
                            <TextInputContainer
                              model={editor.personalInfoVm}
                              field="dateOfBirth"
                              name={`dependent-${index}-dateOfBirth`}
                              label="Date of birth"
                              placeholder="MM/DD/YYYY"
                              mask={masks.americanDate}
                              error={editor.personalInfoVm.validation.getError(
                                'dateOfBirth',
                              )}
                              data-test={`edit-dependent-${index}-dob`}
                            />
                            <TextInputContainer
                              model={editor.personalInfoVm}
                              field="personalTaxpayerIdentificationNumber"
                              name={`dependent-${index}-ssn`}
                              label="SSN or ITIN"
                              placeholder="123-45-6789"
                              mask={masks.ssnOrItin}
                              error={editor.personalInfoVm.validation.getError(
                                'personalTaxpayerIdentificationNumber',
                              )}
                              data-test={`edit-dependent-${index}-tax-id`}
                            />
                            <TextInputContainer
                              model={editor}
                              field="relationship"
                              name={`dependent-${index}-relationship`}
                              label="Relationship"
                              placeholder="e.g. Son, Daughter, Stepchild"
                              error={editor.validation.getError('relationship')}
                              data-test={`edit-dependent-${index}-relationship`}
                            />
                          </InfoGatheringDialogFieldGrid>
                        </div>
                      ))}
                      <div className={styles.addDependentRow}>
                        <Button
                          type="secondary"
                          onClick={vm.addDependent}
                          disabled={vm.submit.pending}
                          data-test="edit-personal-add-dependent-button"
                        >
                          + Add New Dependent
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </Form>
          </InfoGatheringDialogScrollArea>
        </ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        {!vm.isFirstTab && (
          <ModalSheetFooterButtonGroup>
            <Button
              type="secondary"
              onClick={vm.goToPreviousTab}
              disabled={vm.submit.pending}
              data-test="edit-personal-back-button"
            >
              Back
            </Button>
          </ModalSheetFooterButtonGroup>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {!vm.isLastTab && (
            <Button
              type="secondary"
              onClick={vm.skipCurrentTab}
              disabled={vm.submit.pending}
              data-test="edit-personal-skip-button"
            >
              Skip {TAB_LABELS[vm.activeTab]}
            </Button>
          )}
          <Button
            type="primary"
            onClick={vm.submit}
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
            data-test="edit-personal-submit-button"
          >
            {vm.isLastTab ? 'Save & Close' : 'Save & Continue'}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
